import PaymentForm, { PaymentFormRef } from '@/components/common/PaymentForm';
import StripeProvider from '@/components/common/StripeProvider';
import { STRIPE_CONFIG } from '@/config/stripe';
import Messages from '@/core/messages/Messages';
import { getCardBrandIcon } from '@/services/CommonService';
import { UserService } from '@/services/UserService';
import { useAppSelector } from '@/store/StoreHooks';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import './PaymentUI.scss';

interface PaymentCard {
  id: number;
  last4: string;
  brand: string;
  holderName: string;
  expiryMonth: string;
  expiryYear: string;
  isDefault: boolean;
  isSelected?: boolean;
}

interface PaymentUIProps {
  amount: number;
  onPaymentComplete?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  checkIds: number[];
}

const PaymentUI: React.FC<PaymentUIProps> = ({
  amount,
  onPaymentComplete,
  onClose,
  isOpen = true,
  checkIds,
}) => {
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showAddCardForm, setShowAddCardForm] = useState(false);
  const [cardValid, setCardValid] = useState(false);
  const [cardEmpty, setCardEmpty] = useState(false);
  const paymentFormRef = useRef<PaymentFormRef>(null);
  const user = useAppSelector((state) => state.userDetail);
  const [validCard, setValidCard] = useState(false);
  const [checkBoxesData, setCheckBoxesData] = useState({
    acknowledgement: false,
    authorization: false,
  });

  useEffect(() => {
    if (isOpen) {
      loadPaymentMethods();
    }
  }, [isOpen]);

  useEffect(() => {
    if (cardValid) {
      setValidCard(true);
    }
  }, [cardValid]);

  useEffect(() => {
    if (cardEmpty) {
      setValidCard(false);
    }
  }, [cardEmpty]);

  const loadPaymentMethods = async () => {
    try {
      const response = await UserService.getPaymentMethods();
      const paymentMethods = response.data || [];

      // Transform the data to match our interface
      const transformedCards: PaymentCard[] = paymentMethods.map(
        (method: any) => ({
          id: method.id,
          last4: method.card_details?.last4 || '****',
          brand: method.card_details?.brand || 'visa',
          holderName: method.billing_details?.name || 'Card Holder',
          expiryMonth: method.card_details?.exp_month?.toString() || '12',
          expiryYear: method.card_details?.exp_year?.toString() || '26',
          isDefault: method.is_default,
          isSelected: false,
        })
      );

      // Set the first card as selected by default, or the default card if exists
      if (transformedCards.length > 0) {
        const defaultCard = transformedCards.find((card) => card.isDefault);
        const firstCard = transformedCards[0];
        const selectedCard = defaultCard || firstCard;
        setSelectedCardId(selectedCard.id);
        setValidCard(true);
        // Mark the selected card
        transformedCards.forEach((card) => {
          card.isSelected = card.id === selectedCard.id;
        });
      }

      setCards(transformedCards);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const handleCardSelect = (cardId: number) => {
    setSelectedCardId(cardId);
    setValidCard(true);
    setShowAddCardForm(false);
    setCards((prevCards) =>
      prevCards.map((card) => ({
        ...card,
        isSelected: card.id === cardId,
      }))
    );
  };

  const handlePayment = async () => {
    setLoading(true);
    if (showAddCardForm) {
      setSubmitted(true);
    }

    if (
      loading ||
      !validCard ||
      (showAddCardForm
        ? !(checkBoxesData.acknowledgement && checkBoxesData.authorization)
        : false)
    ) {
      return;
    }

    if (showAddCardForm) {
      const response = await paymentFormRef.current?.createPaymentMethod();
      if (response) {
        processPayment({
          source: 'new',
          paymentMethodId: response.paymentMethod.id,
          paymentMethod: {...response.paymentMethod, ...checkBoxesData},
        });
      } else {
        setLoading(false);
      }
    } else {
      processPayment({
        source: 'saved',
        methodId: Number(selectedCardId),
      });
    }
  };

  const processPayment = async ({
    source,
    paymentMethodId,
    methodId,
    paymentMethod,
  }: {
    source: string;
    paymentMethodId?: string;
    methodId?: number;
    paymentMethod?: any;
  }) => {
    setLoading(true);
    try {
      const response = await UserService.processPayment({
        source,
        paymentMethodId,
        methodId,
        paymentMethod,
        checkIds,
      });
      if (response?.data?.status === 'requires_action') {
        const result = await paymentFormRef.current?.confirmCardPayment(
          response.data.data.clientSecret,
          response.data.data.paymentMethodId
        );

        // Payment failed
        if (result?.error) {
          const message = result?.error?.message || Messages.payment_failed;
          toast.error(message);
        } else {
          if (result?.paymentIntent?.status === 'succeeded') {
            if (typeof onPaymentComplete === 'function') {
              onPaymentComplete();
              toast.success(Messages.payment_received);
            }
          } else {
            const message =
              result?.paymentIntent?.last_payment_error?.message ||
              Messages.payment_failed;
            toast.error(message);
          }
        }
      } else if (response?.data?.status === 'success') {
        if (typeof onPaymentComplete === 'function') {
          onPaymentComplete();
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || Messages.payment_failed);
    } finally {
      setLoading(false);
      setSubmitted(false);
    }
  };

  const handleAddNewCard = () => {
    setShowAddCardForm(true);
    setValidCard(false);
    setCards((prev) => {
      return prev.map((card) => ({
        ...card,
        isSelected: false,
      }));
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>Select Payment Method</h2>
          <p>Choose how you'd like to pay {formatAmount(amount)}</p>
          <button
            className="close-btn"
            onClick={() => {
              onClose?.();
              setSubmitted(false);
            }}
          >
            <i className="cs-icon icon-close"></i>
          </button>
        </div>

        {/* Saved Cards Section */}
        <div className="saved-cards-section">
          <div className="section-header">
            <i className="cs-icon icon-card"></i>
            <h3>Your Saved Cards</h3>
          </div>

          <div className="cards-list">
            {cards.map((card) => (
              <div
                key={card.id}
                className={`card-item ${card.isSelected ? 'selected' : ''}`}
                onClick={() => handleCardSelect(card.id)}
              >
                <div className="card-info">
                  <div className="card-icon">
                    {getCardBrandIcon(card.brand)}
                  </div>
                  <div className="card-details">
                    <div className="card-number">
                      **** **** **** {card.last4}
                      {card.isDefault && (
                        <span className="default-badge">
                          <i className="cs-icon icon-check"></i>
                          Default
                        </span>
                      )}
                    </div>
                    <div className="card-holder">{card.holderName}</div>
                    <div className="card-expiry">
                      Expires {card.expiryMonth}/{card.expiryYear}
                    </div>
                  </div>
                </div>

                <div className="card-status">
                  {card.isSelected && <i className="cs-icon icon-check"></i>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add New Card Section */}
        <div className="add-new-card-section">
          <div
            className={`add-card-button ${showAddCardForm ? 'active' : ''}`}
            onClick={handleAddNewCard}
          >
            <div className="add-icon">
              <i className="cs-icon icon-plus"></i>
            </div>
            <div className="add-text">
              <span className="add-title">Add New Card</span>
              <span className="add-subtitle">
                Use a different payment method
              </span>
            </div>
          </div>
        </div>

        <div className="payment-form-container">
          <StripeProvider publishableKey={STRIPE_CONFIG.publishableKey}>
            <div className="payment-form-container-inner">
              <PaymentForm
                showCardForm={showAddCardForm}
                onCardValid={(valid, cardName) => {
                  setCardValid(valid);
                }}
                isCardEmpty={(isEmpty) => {
                  setCardEmpty(isEmpty);
                }}
                isSubmitted={submitted}
                customerEmail={user.email}
                disabled={loading}
                ref={paymentFormRef}
                checkBoxesData={checkBoxesData}
                onCheckBoxChange={(name, value) => {
                  setCheckBoxesData((prev) => ({
                    ...prev,
                    [name]: value,
                  }));
                }}
              />
            </div>
          </StripeProvider>
        </div>
        {/* Security Information */}
        <div className="security-info">
          <div className="security-badge">
            <i className="cs-icon icon-lock"></i>
            <span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="512"
                height="512"
              >
                <g id="_01_align_center" data-name="01 align center">
                  <path d="M11.948,24.009l-.354-.157C11.2,23.679,2,19.524,2,12V5.476A2.983,2.983,0,0,1,4.051,2.644L12,.009l7.949,2.635A2.983,2.983,0,0,1,22,5.476V12c0,8.577-9.288,11.755-9.684,11.887ZM12,2.106,4.684,4.532A.992.992,0,0,0,4,5.476V12c0,5.494,6.44,9.058,8.047,9.861C13.651,21.216,20,18.263,20,12V5.476a.992.992,0,0,0-.684-.944Z" />
                  <path d="M11.111,14.542h-.033a1.872,1.872,0,0,1-1.345-.6l-2.306-2.4L8.868,10.16,11.112,12.5l5.181-5.181,1.414,1.414-5.261,5.261A1.873,1.873,0,0,1,11.111,14.542Z" />
                </g>
              </svg>
              Secure Payment
            </span>
          </div>
          <p>Your payment information is encrypted and secure</p>
        </div>

        {/* Payment Button */}
        <div className="payment-action">
          <button
            className="btn btn--pri"
            onClick={handlePayment}
            disabled={loading}
          >
            {loading ? 'Processing...' : `Pay ${formatAmount(amount)}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentUI;
